/**
 * Data store — iki katmanlı:
 *
 * 1. BUILD verisi  → src/data/siteData.js  (build'e gömülür, herkes görür)
 * 2. LOCAL verisi  → localStorage          (admin paneli yazar, sadece bu tarayıcıda)
 *
 * Okuma önceliği: localStorage > siteData.js > hardcoded defaults
 * Böylece admin panelinde yaptığın değişiklikler anında local'de görünür,
 * "Siteyi Güncelle" ile export edip build alınca Hostinger'da da görünür.
 */

import { siteData } from "../data/siteData";

const KEYS = {
  settings:   "ygs_settings",
  products:   "ygs_products",
  services:   "ygs_services",
  faqs:       "ygs_faqs",
  references: "ygs_references",
  about:      "ygs_about",
  auth:       "ygs_admin_auth",
};

// ─── Auth ────────────────────────────────────────────────────────────────────
const ADMIN_USER = "azemsahin";
const ADMIN_PASS = "kai.zone";

export function adminLogin(username, password) {
  if (username === ADMIN_USER && password === ADMIN_PASS) {
    localStorage.setItem(KEYS.auth, "1");
    return true;
  }
  return false;
}
export function adminLogout()    { localStorage.removeItem(KEYS.auth); }
export function isAdminLoggedIn(){ return localStorage.getItem(KEYS.auth) === "1"; }

// ─── Generic helpers ─────────────────────────────────────────────────────────
function read(lsKey, buildFallback, hardFallback) {
  // 1. localStorage (admin panel değişiklikleri)
  try {
    const raw = localStorage.getItem(lsKey);
    if (raw) return JSON.parse(raw);
  } catch {}
  // 2. siteData.js (son export)
  if (buildFallback !== undefined && buildFallback !== null) return buildFallback;
  // 3. hardcoded defaults
  return hardFallback;
}
function write(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}
function uid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// ─── Settings ────────────────────────────────────────────────────────────────
export const DEFAULT_SETTINGS = {
  phone:            "+90 212 555 0 144",
  phone2:           "",
  email:            "info@ygs.com.tr",
  address:          "İkitelli OSB, Atatürk Bulvarı No: 124, Başakşehir / İstanbul",
  whatsapp_phone:   "",
  whatsapp_message_tr: "Merhaba YGS, web sitenizden ulaşıyorum. Bir teklif almak istiyorum.",
  whatsapp_message_en: "Hi YGS, I'm reaching out from your website. I'd like to request a quote.",
  instagram:        "https://instagram.com",
  linkedin:         "https://linkedin.com",
  trust_badge_text: "Genelde 24 saat içinde dönüş yapılır · 7/24 servis garantisi",
  hero_image:       "https://static.prod-images.emergentagent.com/jobs/169b1244-32c7-4b71-b5b4-2579e0928bee/images/f6d128ef636176f6c5a40a049b5b8654982b918a209287274cb9c1e07e79b5c5.png",
  about_image:      "https://images.unsplash.com/photo-1772299121503-cd62a57e3a26?crop=entropy&cs=srgb&fm=jpg&q=85",
  map_embed_url:    "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3009.5036929725823!2d28.79!3d41.078!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14caa56c2b1f8b65%3A0x0!2zNDHCsDA0JzQwLjgiTiAyOMKwNDcnMjQuMCJF!5e0!3m2!1str!2str!4v1700000000000!5m2!1str!2str",
  // Kayan bant — boş array = gizle, null = gizle, dolu = göster
  band_items: ["ISO 9001", "CE Certified", "TSE Approved", "EN 16005", "ISO 45001", "10 Yıl Garanti"],
  // Hero mini rozetler
  hero_badges: [
    { icon: "Shield", label: "CE / TSE" },
    { icon: "Wrench", label: "7/24 Servis" },
    { icon: "Cpu",    label: "IoT Ready" },
  ],
  // İstatistikler
  stats_heading: "Sayılarla YGS",
  stats_sub:     "İki dekadı aşan tecrübe, binlerce başarıyla tamamlanmış proje.",
  stats_items: [
    { value: "20+",    label: "Yıllık Deneyim" },
    { value: "2.500+", label: "Tamamlanan Proje" },
    { value: "81",     label: "İl Genelinde Hizmet" },
    { value: "%99,8",  label: "Müşteri Memnuniyeti" },
  ],
};
export const getSettings  = ()     => read(KEYS.settings,   siteData?.settings,   DEFAULT_SETTINGS);
export const saveSettings = (data) => write(KEYS.settings, { ...DEFAULT_SETTINGS, ...data });

// ─── Products ────────────────────────────────────────────────────────────────
export const DEFAULT_PRODUCTS = [
  {
    id: "p1", slug: "otomatik-kapilar", lang: "tr", order: 1, active: true,
    title: "Otomatik Kapılar",
    short_desc: "Cam, fotoselli, döner ve hermetik tipte sessiz çalışan otomatik kapı sistemleri.",
    long_desc: "Ofis, hastane, alışveriş merkezi ve otel girişleri için sessiz, hızlı ve güvenli otomatik kapı çözümleri. EN 16005 standartlarına uyumlu, acil durum açılım fonksiyonlu.",
    hero_image: "https://images.unsplash.com/photo-1700308232171-aa0d87ee1a88?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    gallery: [],
    features: ["Sessiz fırçasız motor", "Acil durum açılımı", "Sensör entegrasyonu", "EN 16005 uyumlu", "10 yıl mekanik garanti"],
    specs: [
      { label: "Açılım Hızı", value: "0.6 m/s" },
      { label: "Maks. Kanat Ağırlığı", value: "180 kg" },
      { label: "Sertifika", value: "CE, EN 16005, TSE" },
      { label: "Garanti", value: "10 Yıl Mekanik / 2 Yıl Elektronik" },
    ],
  },
  {
    id: "p2", slug: "kepenk-sistemleri", lang: "tr", order: 2, active: true,
    title: "Kepenk Sistemleri",
    short_desc: "Hırsızlığa ve darbeye dayanıklı motorlu kepenkler; ofis, mağaza ve depolar için.",
    long_desc: "Ticari mağaza ve depo cepheleri için sessiz motorlu, çift cidarlı çelik panelli kepenk sistemleri.",
    hero_image: "https://images.unsplash.com/photo-1776316170808-f59232c1a85e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    gallery: [],
    features: ["Galvanize çelik panel", "Sessiz motor", "Akıllı kontrol", "Anti-vandal kilit"],
    specs: [
      { label: "Maks. Genişlik", value: "9.000 mm" },
      { label: "Maks. Yükseklik", value: "5.500 mm" },
      { label: "Sertifika", value: "CE, ISO 9001" },
    ],
  },
  {
    id: "p3", slug: "endustriyel-kapilar", lang: "tr", order: 3, active: true,
    title: "Endüstriyel Kapılar",
    short_desc: "Yüksek devirli seksiyonel ve hızlı PVC endüstriyel kapı çözümleri.",
    long_desc: "Lojistik depoları, üretim tesisleri ve soğuk hava odaları için yüksek devirli (3 m/s) PVC ve seksiyonel paneli endüstriyel kapılar.",
    hero_image: "https://images.unsplash.com/photo-1769497165127-a6400eb3edf0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    gallery: [],
    features: ["3 m/s açılım", "Termal yalıtım", "Ağır hizmet", "Soğuk oda uyumlu"],
    specs: [
      { label: "Maks. Genişlik", value: "12.000 mm" },
      { label: "Açılım Hızı", value: "3 m/s" },
      { label: "Sıcaklık Aralığı", value: "-30°C / +50°C" },
    ],
  },
];
export const getProducts   = ()     => read(KEYS.products,   siteData?.products,   DEFAULT_PRODUCTS);
export const saveProducts  = (list) => write(KEYS.products, list);
export const addProduct    = (item) => { const l = getProducts(); l.push({ ...item, id: uid() }); saveProducts(l); };
export const updateProduct = (item) => { saveProducts(getProducts().map(p => p.id === item.id ? item : p)); };
export const deleteProduct = (id)   => { saveProducts(getProducts().filter(p => p.id !== id)); };

// ─── Services ────────────────────────────────────────────────────────────────
export const DEFAULT_SERVICES = [
  { id: "s1", order: 1, active: true, lang: "tr", icon: "ClipboardCheck", title: "Keşif & Projelendirme", desc: "Sahada ücretsiz keşif, ölçü, statik analiz ve teknik proje hazırlama." },
  { id: "s2", order: 2, active: true, lang: "tr", icon: "Factory",        title: "Üretim",                desc: "Avrupa standartlarında özel ölçü üretim, kalite kontrol ve test laboratuvarı." },
  { id: "s3", order: 3, active: true, lang: "tr", icon: "Wrench",         title: "Montaj & Devreye Alma", desc: "Sertifikalı ekiplerle hızlı kurulum, kontrol ve eğitim." },
  { id: "s4", order: 4, active: true, lang: "tr", icon: "Headphones",     title: "7/24 Servis",           desc: "Türkiye genelinde 4 saat içinde müdahale garantili teknik destek." },
  { id: "s5", order: 5, active: true, lang: "tr", icon: "CalendarCheck",  title: "Bakım Sözleşmeleri",    desc: "Periyodik bakım, yedek parça ve performans raporlama hizmetleri." },
  { id: "s6", order: 6, active: true, lang: "tr", icon: "Wifi",           title: "Uzaktan İzleme",        desc: "IoT tabanlı uzaktan izleme ve kestirimci bakım altyapısı." },
];
export const getServices   = ()     => read(KEYS.services,   siteData?.services,   DEFAULT_SERVICES);
export const saveServices  = (list) => write(KEYS.services, list);
export const addService    = (item) => { const l = getServices(); l.push({ ...item, id: uid() }); saveServices(l); };
export const updateService = (item) => { saveServices(getServices().map(s => s.id === item.id ? item : s)); };
export const deleteService = (id)   => { saveServices(getServices().filter(s => s.id !== id)); };

// ─── FAQs ────────────────────────────────────────────────────────────────────
export const DEFAULT_FAQS = [
  { id: "f1", order: 1, active: true, lang: "tr", question: "Keşif ücretli mi?",                   answer: "Hayır. Türkiye genelinde keşif tamamen ücretsizdir. Talebinizi ilettiğinizde 24 saat içinde sahada oluyoruz." },
  { id: "f2", order: 2, active: true, lang: "tr", question: "Garanti süreniz ne kadar?",            answer: "Tüm sistemlerimizde standart garanti süresi 24 aydır; otomatik kapılarda mekanik aksam için 10 yıla kadar uzatılabilir." },
  { id: "f3", order: 3, active: true, lang: "tr", question: "Yedek parça stoklarınız var mı?",      answer: "Evet, İstanbul'daki ana lojistik merkezimizde 5.000+ kalemlik yedek parça envanteri bulunmaktadır." },
  { id: "f4", order: 4, active: true, lang: "tr", question: "Bakım sözleşmesi nasıl ilerliyor?",    answer: "Yıllık 2/4/6 ziyaret seçenekleri sunuyoruz. Her ziyaret sonrası dijital bakım raporu paylaşılır." },
  { id: "f5", order: 5, active: true, lang: "tr", question: "Mevcut sistemleri yeniliyor musunuz?", answer: "Evet, mevcut otomatik kapı ve kepenk sistemlerini modernize ediyor; motor, kontrol ve sensör yenileme yapıyoruz." },
  { id: "f6", order: 6, active: true, lang: "tr", question: "Hangi şehirlerde hizmet veriyorsunuz?",answer: "81 il genelinde aktif servis ağımız bulunmaktadır. Detay için bize ulaşın." },
];
export const getFaqs   = ()     => read(KEYS.faqs,       siteData?.faqs,       DEFAULT_FAQS);
export const saveFaqs  = (list) => write(KEYS.faqs, list);
export const addFaq    = (item) => { const l = getFaqs(); l.push({ ...item, id: uid() }); saveFaqs(l); };
export const updateFaq = (item) => { saveFaqs(getFaqs().map(f => f.id === item.id ? item : f)); };
export const deleteFaq = (id)   => { saveFaqs(getFaqs().filter(f => f.id !== id)); };

// ─── References ──────────────────────────────────────────────────────────────
export const DEFAULT_REFERENCES = [
  { id: "r1", order: 1, active: true, name: "Aksa İnşaat",   logo_url: "", website: "", description: "", project_image: "" },
  { id: "r2", order: 2, active: true, name: "Yapı Merkezi",  logo_url: "", website: "", description: "", project_image: "" },
  { id: "r3", order: 3, active: true, name: "Doğuş Holding", logo_url: "", website: "", description: "", project_image: "" },
  { id: "r4", order: 4, active: true, name: "Tav Yapı",      logo_url: "", website: "", description: "", project_image: "" },
  { id: "r5", order: 5, active: true, name: "Limak Holding", logo_url: "", website: "", description: "", project_image: "" },
  { id: "r6", order: 6, active: true, name: "Rönesans",      logo_url: "", website: "", description: "", project_image: "" },
];
export const getReferences   = ()     => read(KEYS.references,  siteData?.references,  DEFAULT_REFERENCES);
export const saveReferences  = (list) => write(KEYS.references, list);
export const addReference    = (item) => { const l = getReferences(); l.push({ ...item, id: uid() }); saveReferences(l); };
export const updateReference = (item) => { saveReferences(getReferences().map(r => r.id === item.id ? item : r)); };
export const deleteReference = (id)   => { saveReferences(getReferences().filter(r => r.id !== id)); };

// ─── About ───────────────────────────────────────────────────────────────────
export const DEFAULT_ABOUT = {
  lang: "tr",
  heading: "20 yılı aşkın süredir mühendislik ve güvenliği aynı çatıda buluşturuyoruz",
  p1: "YGS Yapı Güvenlik Sistemleri, 2003 yılında İstanbul'da kurulmuş; otomatik kapı, kepenk ve endüstriyel kapı sistemlerinde uzmanlaşmış bir mühendislik şirketidir.",
  p2: "Sertifikalı ekibimiz, üretim tesisimiz ve geniş servis ağımızla; ofislerden lojistik merkezlerine, hastanelerden alışveriş merkezlerine kadar her ölçekte projeye çözüm sunarız.",
  bullets: ["ISO 9001 & ISO 45001 sertifikalı üretim", "CE ve TSE uyumlu sistemler", "Türkiye'nin 81 ilinde servis ağı", "120+ uzman saha ekibi"],
  image: "",
};
export const getAbout  = ()     => read(KEYS.about, siteData?.about, DEFAULT_ABOUT);
export const saveAbout = (data) => write(KEYS.about, data);

// ─── Export (build için) ──────────────────────────────────────────────────────
/**
 * Tüm mevcut veriyi siteData.js formatında string olarak döner.
 * Admin panelindeki "Siteyi Güncelle" butonu bunu dosya olarak indirir.
 */
export function exportSiteData() {
  const data = {
    settings:   getSettings(),
    products:   getProducts(),
    services:   getServices(),
    faqs:       getFaqs(),
    references: getReferences(),
    about:      getAbout(),
  };
  const now = new Date().toLocaleString("tr-TR");
  return `/**
 * SITE DATA — Bu dosyayı elle düzenleme.
 * Admin panelinde (/kai) düzenle → "Siteyi Güncelle" butonuna bas
 * → indirilen siteData.js dosyasını src/data/ klasörüne koy → npm run build → Hostinger'a yükle.
 *
 * Son güncelleme: ${now}
 */

export const siteData = ${JSON.stringify(data, null, 2)};
`;
}

// ─── Image helpers ────────────────────────────────────────────────────────────
export function compressToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const MAX = 900;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          if (width > height) { height = Math.round((height * MAX) / width); width = MAX; }
          else { width = Math.round((width * MAX) / height); height = MAX; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}
